import type { ModuleData, ModuleId } from '../content'
import type { GroupId } from '../config/systemConfig'
import { getFamiliesForModule } from '../content'
import { getFamilyMasteryKey } from './familyMasteryEngine'
import { hasActiveDecay } from './masteryDecay'
import type { FamilyMasteryState } from './familyMasteryEngine'

/**
 * Emotion hint for the mascot layer. Defined locally to avoid
 * domain (mastery) → presentation (mascot) dependency (DD-V-4).
 * Must stay in sync with DjayEmotion in mascot/DjayAvatar.tsx.
 */
export type RecommendationEmotion =
  | 'thinking'
  | 'encouraging'
  | 'pointing'
  | 'celebrating'

export interface DjayRecommendation {
  moduleId: ModuleId
  reason: string
  emotion: RecommendationEmotion
  priority: 'decay' | 'struggling' | 'continue' | 'new'
}

export function getDjayRecommendation(
  familyMastery: Record<string, FamilyMasteryState>,
  modules: ModuleData[],
  selectedGroup: GroupId,
  getModuleMasteryForGroup: (moduleId: ModuleId) => number,
  getAnsweredQuestions: (moduleId: ModuleId) => unknown[],
): DjayRecommendation | null {
  if (!modules || modules.length === 0) return null

  // Filter modules into categories
  const inProgressModules = modules.filter((m) => {
    const mastery = getModuleMasteryForGroup(m.id)
    const answered = getAnsweredQuestions(m.id).length
    return (answered > 0 || mastery > 0) && mastery < 95
  })

  const newModules = modules.filter((m) => {
    const mastery = getModuleMasteryForGroup(m.id)
    const answered = getAnsweredQuestions(m.id).length
    return answered === 0 && mastery === 0
  })

  // 1. Decay-based
  if (inProgressModules.length > 0) {
    let maxDecayCount = 0
    let decayModule: ModuleData | null = null

    for (const m of inProgressModules) {
      const families = getFamiliesForModule(m.id)
      let decayCount = 0
      for (const f of families) {
        const key = getFamilyMasteryKey(f.familyId, selectedGroup)
        if (hasActiveDecay(familyMastery[key])) {
          decayCount++
        }
      }
      if (decayCount > maxDecayCount) {
        maxDecayCount = decayCount
        decayModule = m
      }
    }

    if (decayModule) {
      return {
        moduleId: decayModule.id,
        reason: `Je hebt ${decayModule.title} al een tijdje niet geoefend — tijd voor een opfrisser!`,
        emotion: 'thinking',
        priority: 'decay',
      }
    }
  }

  // 2. Struggling
  if (inProgressModules.length > 0) {
    let weakestModule: ModuleData | null = null
    let lowestMastery = 100

    for (const m of inProgressModules) {
      const mastery = getModuleMasteryForGroup(m.id)
      if (mastery < 40 && mastery < lowestMastery) {
        lowestMastery = mastery
        weakestModule = m
      }
    }

    if (weakestModule) {
      return {
        moduleId: weakestModule.id,
        reason: `${weakestModule.title} kan nog beter — laten we samen oefenen!`,
        emotion: 'encouraging',
        priority: 'struggling',
      }
    }
  }

  // 3. Continue
  if (inProgressModules.length > 0) {
    let continueModule = inProgressModules[0]
    let lowestMastery = getModuleMasteryForGroup(continueModule.id)

    for (const m of inProgressModules) {
      const mastery = getModuleMasteryForGroup(m.id)
      if (mastery < lowestMastery) {
        lowestMastery = mastery
        continueModule = m
      }
    }

    return {
      moduleId: continueModule.id,
      reason: `Ga verder met ${continueModule.title} — je bent lekker op weg!`,
      emotion: 'pointing',
      priority: 'continue',
    }
  }

  // 4. New module
  if (newModules.length > 0) {
    const newModule = newModules[0]
    return {
      moduleId: newModule.id,
      reason: `Tijd voor iets nieuws! Laten we ${newModule.title} ontdekken.`,
      emotion: 'celebrating',
      priority: 'new',
    }
  }

  // Fallback if everything is mastered
  return {
    moduleId: modules[0].id,
    reason: `Je hebt alles fantastisch gedaan! Kies een vak om te herhalen.`,
    emotion: 'celebrating',
    priority: 'continue',
  }
}
